import { defineStore } from 'pinia'
import { useAuthStore } from './useAuthStore'
import type { NotificationSettings, NotificationTiming } from '../types'
import { getDocument, setDocument } from '../services/firestoreService'

// 權限狀態類型（相容 Web 和 Capacitor）
export type PermissionState = 'granted' | 'denied' | 'default' | 'prompt'

const DEFAULT_SETTINGS: NotificationSettings = {
  enabled: false,
  timings: ['7days', '1day', 'morning'],
  fcmToken: null,
}

export const useNotificationStore = defineStore('notification', {
  state: () => ({
    settings: { ...DEFAULT_SETTINGS } as NotificationSettings,
    loaded: false,
    permission: 'default' as PermissionState,
  }),

  getters: {
    // 推播是否啟用：有權限且有選擇任何提醒時間點
    isEnabled: (state) => state.permission === 'granted' && state.settings.timings.length > 0,
    hasPermission: (state) => state.permission === 'granted',
    needsPermission: (state) => state.permission === 'default' || state.permission === 'prompt',
  },

  actions: {
    /**
     * 載入推播設定
     */
    async loadSettings() {
      const auth = useAuthStore()
      if (!auth.uid) {
        return
      }

      try {
        const path = `users/${auth.uid}/settings/notifications`
        const doc = await getDocument<NotificationSettings>(path)

        if (doc) {
          this.settings = {
            ...DEFAULT_SETTINGS,
            ...doc,
            // 確保 fcmToken 不是 undefined
            fcmToken: doc.fcmToken ?? null,
          } as NotificationSettings
        } else {
          // 使用預設設定
          this.settings = { ...DEFAULT_SETTINGS }
        }

        this.loaded = true
      } catch (error) {
        this.settings = { ...DEFAULT_SETTINGS }
        this.loaded = true
      }
    },

    /**
     * 儲存推播設定
     */
    async saveSettings(settings: Partial<NotificationSettings>): Promise<boolean> {
      const auth = useAuthStore()
      if (!auth.uid) {
        return false
      }

      try {
        // 合併設定，並確保 fcmToken 不會是 undefined
        const merged = {
          ...this.settings,
          ...settings,
        }

        // 清理 undefined 值，將它們轉換為 null 或移除
        const cleanSettings: NotificationSettings = {
          enabled: merged.enabled,
          timings: merged.timings,
          fcmToken: merged.fcmToken ?? null,
        }

        // 移除值為 null 的欄位（可選）
        const firestoreData: any = {
          enabled: cleanSettings.enabled,
          timings: cleanSettings.timings,
        }

        if (cleanSettings.fcmToken !== null) {
          firestoreData.fcmToken = cleanSettings.fcmToken
        }

        // 更新本地狀態
        this.settings = cleanSettings

        // 儲存到 Firestore
        const path = `users/${auth.uid}/settings/notifications`
        await setDocument(path, firestoreData)

        return true
      } catch (error) {
        throw error
      }
    },

    /**
     * 更新推播權限狀態
     */
    updatePermission(permission: PermissionState) {
      this.permission = permission
    },

    /**
     * 啟用推播
     */
    async enableNotifications() {
      await this.saveSettings({ enabled: true })
    },

    /**
     * 停用推播
     */
    async disableNotifications() {
      await this.saveSettings({ enabled: false })
    },

    /**
     * 更新提醒時間點
     */
    async updateTimings(timings: NotificationTiming[]): Promise<boolean> {
      const result = await this.saveSettings({ timings })
      return result
    },

    /**
     * 儲存 FCM Token
     */
    async saveFCMToken(token: string) {
      await this.saveSettings({ fcmToken: token })
    },

    /**
     * 重置設定
     */
    async resetSettings() {
      await this.saveSettings(DEFAULT_SETTINGS)
    },
  },
})
